import { Controller } from "@hotwired/stimulus"
import { confirmDialog, successDialog, errorDialog } from "../helpers/swal_helper"

export default class extends Controller {
  static values = {
    modelName: String,
    confirmText: String,
    successAction: String,
    successRedirectPath: String,
    url: String,
    ids: Array,
  }

  async confirm(event) {
    Rails.stopEverything(event);

    const link = event.currentTarget
    const url = link.href
    const tr = link.closest("tr")
    const id = tr?.dataset.rowId

    if (id) {
      // defines ids so that row can be faded out
      this.idsValue = [parseInt(id, 10)]
    }

    const method = link.dataset.method?.toUpperCase() || "DELETE"
    const modelName = this.modelNameValue || this._guessModelName(link)
    const confirmText = this.confirmTextValue || `Are you sure you want to delete this ${modelName}?`

    const confirmed = await confirmDialog({ text: confirmText })

    if (!confirmed) {
      errorDialog("Cancelled", `${modelName} was not deleted.`)
      return
    }

    Rails.ajax({
      url: url,
      type: method,
      dataType: "json",
      success: async (data, status, xhr) => {
        let message

        if (xhr.responseText && xhr.responseText.length > 0) {
          try {
            const json = JSON.parse(xhr.responseText)
            message = json.message || `${modelName} updated`
          } catch {
            message = `${modelName} updated`
          }
        } else {
          message = method === "DELETE" ? `${modelName} deleted` : `${modelName} updated`
        }

        await this._handleSuccess(message)
      },
      error: (xhr) => {
        let msg = "Something went wrong."
        try {
          const json = JSON.parse(xhr.responseText)
          if (json.error) msg = json.error
        } catch {}
        errorDialog("Error", msg)
      }
    })
  }

  async confirmBatch(event) {
    event.preventDefault()

    const query = new URLSearchParams()
    this.idsValue.forEach(id => query.append("ids[]", id))

    const queryString = query.toString()

    const modelName = this.modelNameValue || "records"
    const confirmText = this.messageValue || `Are you sure you want to delete the selected ${modelName}?`

    confirmDialog({ text: confirmText }).then((confirmed) => {
      if (!confirmed) {
        errorDialog("Cancelled", `${modelName} were not deleted.`)
        return
      }

      Rails.ajax({
        url: `${this.urlValue}?${queryString}`,
        type: "DELETE",
        success: async () => {
          await this._handleSuccess(`Selected ${modelName} have been deleted.`)

          // cause "Batch Actions" to be disabled
          window.batch_action_ids = []
        },
        error: () => {
          errorDialog("Error", "Something went wrong.")
        }
      })
    })
  }

  async _handleSuccess(message) {
    // Add small delay to ensure dialog cleanup before navigation
    await new Promise(resolve => setTimeout(resolve, 200))

    await successDialog(message)

    switch (this.successActionValue) {
      case "refresh":
        window.location.reload()
        break

      case "redirect":
        Turbolinks.visit(this.successRedirectPathValue)
        break

      case "fadeOutRows":
        this._fadeOutSelectedRows()
        break
    }
  }

  _fadeOutSelectedRows() {
    if (!this.hasIdsValue) return

    this.idsValue.forEach(id => {
      const row = document.querySelector(`[data-row-id="${id}"]`)
      if (row) {
        row.style.transition = "opacity 0.4s ease"
        row.style.opacity = 0

        setTimeout(() => row.remove(), 400)
      }
    })
  }

  _guessModelName(link) {
    const datasetModel = link.dataset.model
    if (datasetModel) return this._formatModelName(datasetModel)

    const url = new URL(link.href)
    const pathSegments = url.pathname.split("/")

    const guessed = pathSegments[pathSegments.length - 2] || "item"
    return this._formatModelName(guessed)
  }

  _formatModelName(rawName) {
    // Convert underscores to spaces, singularize (naively), capitalize words
    const singular = rawName.replace(/s$/, '') // basic singularization (works for most)
    return singular
      .replace(/_/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase())
  }

  async confirmFieldBeforeSubmit(event) {
    event.preventDefault();
    const form = event.target;
    const fieldSelector = this.hasFieldSelectorValue ? this.fieldSelectorValue : 'input[type="email"]';
    const field = form.querySelector(fieldSelector);
    const value = field ? field.value.trim() : "";
    const confirmText = this.confirmTextValue || "Are you sure you want to proceed without this field filled?";
    const confirmButtonText = this.confirmButtonTextValue || "Proceed Anyway";
    const cancelButtonText = this.cancelButtonTextValue || "Amend";
    const submitButton = form.querySelector('button[type="submit"], input[type="submit"]');

    if (!value) {
      if (submitButton) submitButton.disabled = false;
      const confirmed = await confirmDialog({
        text: confirmText,
        confirmButtonText,
        cancelButtonText
      });
      if (!confirmed) {
        // User wants to amend, re-enable submit button
        if (submitButton) submitButton.disabled = false;
        return;
      }
      // User wants to proceed, submit the form
      if (submitButton) submitButton.disabled = false;
      form.submit();
      return;
    }
    // If field is present, submit as normal
    if (submitButton) submitButton.disabled = false;
    form.submit();
  }
}
